import * as React from "react"
import { format } from "date-fns"
import { enGB } from "date-fns/locale"
import { CalendarIcon, ClockIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"

export function DateTimePicker({
  value,
  onChange,
  minDate,
  placeholder = "Pick date & time",
  className,
}: {
  value?: Date
  onChange: (date: Date | undefined) => void
  minDate?: Date
  placeholder?: string
  className?: string
}) {
  const [open, setOpen] = React.useState(false)

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5)

  const selectedHour = value?.getHours() ?? new Date().getHours()
  const selectedMinute = value ? value.getMinutes() : 0

  const hourRef = React.useRef<HTMLDivElement>(null)
  const minuteRef = React.useRef<HTMLDivElement>(null)

  // Scroll to selected time when popover opens
  React.useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => {
      hourRef.current
        ?.querySelector("[data-active=true]")
        ?.scrollIntoView({ block: "center" })
      minuteRef.current
        ?.querySelector("[data-active=true]")
        ?.scrollIntoView({ block: "center" })
    }, 50)
    return () => clearTimeout(timer)
  }, [open])

  function handleDateSelect(day: Date | undefined) {
    if (!day) return
    const next = new Date(day)
    next.setHours(selectedHour, selectedMinute, 0, 0)
    onChange(next)
  }

  function handleHourClick(hour: number) {
    const next = value ? new Date(value) : new Date()
    if (!value) {
      next.setSeconds(0, 0)
    }
    next.setHours(hour)
    onChange(next)
  }

  function handleMinuteClick(minute: number) {
    const next = value ? new Date(value) : new Date()
    if (!value) {
      next.setSeconds(0, 0)
    }
    next.setMinutes(minute)
    onChange(next)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 w-full justify-start gap-2 px-3 text-left text-sm font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="size-4 text-warm-grey" />
          {value ? (
            format(value, "d MMM yyyy", { locale: enGB })
          ) : (
            <span className="text-warm-grey">{placeholder}</span>
          )}
          {value && (
            <>
              <span className="text-warm-grey/40">|</span>
              <ClockIcon className="size-3.5 text-warm-grey" />
              {format(value, "HH:mm")}
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Calendar */}
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            disabled={minDate ? { before: minDate } : undefined}
            locale={enGB}
            className="p-3"
          />

          {/* Time pickers */}
          <div className="flex border-l border-stone/20">
            {/* Hours */}
            <ScrollArea className="h-[304px] w-16">
              <div ref={hourRef} className="p-1.5">
                <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-warm-grey">
                  Hr
                </p>
                {hours.map((h) => {
                  const isActive = h === selectedHour
                  return (
                    <button
                      key={h}
                      type="button"
                      data-active={isActive}
                      onClick={() => handleHourClick(h)}
                      className={cn(
                        "flex w-full items-center justify-center rounded-md py-1.5 text-xs transition-colors",
                        isActive
                          ? "bg-red font-semibold text-white"
                          : "text-charcoal hover:bg-stone/20",
                      )}
                    >
                      {h.toString().padStart(2, "0")}
                    </button>
                  )
                })}
              </div>
            </ScrollArea>

            {/* Minutes */}
            <ScrollArea className="h-[304px] w-16 border-l border-stone/15">
              <div ref={minuteRef} className="p-1.5">
                <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-warm-grey">
                  Min
                </p>
                {minutes.map((m) => {
                  const isActive = m === selectedMinute
                  return (
                    <button
                      key={m}
                      type="button"
                      data-active={isActive}
                      onClick={() => handleMinuteClick(m)}
                      className={cn(
                        "flex w-full items-center justify-center rounded-md py-1.5 text-xs transition-colors",
                        isActive
                          ? "bg-red font-semibold text-white"
                          : "text-charcoal hover:bg-stone/20",
                      )}
                    >
                      {m.toString().padStart(2, "0")}
                    </button>
                  )
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
